<template>
  <BaseModal 
    :show="isOpen"
    size="lg"
    :title="mode === 'create' ? $t('app.views.users.modals.create_title') : $t('app.views.users.modals.edit_title')"
    show-actions
    :is-editing="mode === 'edit'"
    :loading="saving"
    :save-text="t('app.users.modal.save')"
    :loading-text="t('app.users.modal.saving')"
    save-class="bg-indigo-600 hover:bg-indigo-700"
    @close="$emit('close')"
    @save="handleSubmit"
  >
    <div class="space-y-3 sm:space-y-4">
      <!-- Form -->
      <form @submit.prevent="handleSubmit">
        <!-- Full Name -->
        <UserFormField
          v-model="form.full_name"
          :label="t('app.users.modal.full_name')"
          :placeholder="t('app.users.modal.full_name_placeholder')"
          :error="fullNameError"
          :touched="fullNameTouched"
          required
          @input="fullNameTouched = true"
        />

        <!-- Email -->
        <UserFormField
          v-model="form.email"
          type="email"
          :label="t('app.users.modal.email')"
          :placeholder="t('app.users.modal.email_placeholder')"
          :error="emailError"
          :touched="emailTouched"
          required
          @blur="() => validateEmail(form.email)"
          @input="emailTouched = true"
        />

        <!-- Password -->
        <UserPasswordField
          v-model="form.password"
          :label="t('app.users.modal.password')"
          :placeholder="t('app.users.modal.password_placeholder')"
          :optional="mode === 'edit'"
          :required="mode === 'create'"
          :hint="t('app.users.modal.password_hint')"
          :errors="passwordErrors"
          :touched="passwordTouched"
          :minlength="8"
          @blur="() => validatePassword(form.password)"
          @input="passwordTouched = true"
        />

        <!-- Role -->
        <UserSelectField
          v-model="form.role"
          :label="t('app.users.modal.role')"
          required
        >
          <option value="admin">
            {{ t('app.users.roles.admin') }}
          </option>
          <option value="staff">
            {{ t('app.users.roles.staff') }}
          </option>
          <option value="customer">
            {{ t('app.users.roles.customer') }}
          </option>
        </UserSelectField>

        <!-- Staff Type (only show if role is staff) -->
        <UserSelectField
          v-if="form.role === 'staff'"
          :model-value="form.staff_type || ''"
          :label="t('app.users.modal.staff_type')"
          :hint="t('app.users.modal.staff_type_hint')"
          required
          @update:model-value="(value) => form.staff_type = value as 'waiter' | 'cashier' | 'kitchen' | 'general' | null"
        >
          <option value="">
            {{ t('app.users.modal.select_staff_type') }}
          </option>
          <option value="waiter">
            {{ t('app.users.staff_types.waiter') }}
          </option>
          <option value="cashier">
            {{ t('app.users.staff_types.cashier') }}
          </option>
          <option value="kitchen">
            {{ t('app.users.staff_types.kitchen') }}
          </option>
          <option value="general">
            {{ t('app.users.staff_types.general') }}
          </option>
        </UserSelectField>

        <!-- Active Status -->
        <UserCheckboxField
          id="is_active"
          v-model="form.is_active"
          :label="t('app.users.modal.is_active')"
        />

        <!-- Subscription Limit Alert -->
        <SubscriptionLimitAlert
          v-if="subscriptionLimitError"
          :message="subscriptionLimitError"
          :dismissible="false"
        />
          
        <!-- Generic Error Message -->
        <div
          v-else-if="errorMessage"
          class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
        >
          <p class="text-sm text-red-600 dark:text-red-400">
            {{ errorMessage }}
          </p>
        </div>
      </form>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseModal from '@/components/common/BaseModal.vue';
import {
  UserIcon,
  EnvelopeIcon,
  LockClosedIcon
} from '@heroicons/vue/24/outline';
import restaurantUsersService, { type RestaurantUser } from '@/services/restaurantUsersService';
import SubscriptionLimitAlert from '@/components/subscription/SubscriptionLimitAlert.vue';
import UserFormField from './form/UserFormField.vue';
import UserPasswordField from './form/UserPasswordField.vue';
import UserSelectField from './form/UserSelectField.vue';
import UserCheckboxField from './form/UserCheckboxField.vue';
import { useUserFormValidation, type UserFormData } from '@/composables/useUserFormValidation';
import { useUserFormSubmission } from '@/composables/useUserFormSubmission';

const { t } = useI18n();

interface Props {
  isOpen: boolean;
  user: RestaurantUser | null;
  mode: 'create' | 'edit';
}

const props = defineProps<Props>();
const emit = defineEmits<{
  close: [];
  saved: [];
}>();

const form = ref<UserFormData & { id?: number }>({
  email: '',
  full_name: '',
  password: '',
  role: 'staff',
  staff_type: null,
  is_active: true
});

// Use validation composable
const {
  fullNameError,
  fullNameTouched,
  emailError,
  emailTouched,
  passwordErrors,
  passwordTouched,
  validateFullName,
  validateEmail,
  validatePassword,
  validateForm,
  resetValidation,
  setBackendErrors
} = useUserFormValidation(props.mode);

// Use submission composable
const {
  saving,
  errorMessage,
  subscriptionLimitError,
  submitUserForm,
  resetSubmissionState
} = useUserFormSubmission();

// Function to reset form to initial state
function resetForm() {
  form.value = {
    email: '',
    full_name: '',
    password: '',
    role: 'staff',
    staff_type: null,
    is_active: true
  } as UserFormData & { id?: number };
  resetValidation();
  resetSubmissionState();
}

// Watch for user changes to populate form in edit mode
watch(() => props.user, (newUser) => {
  if (newUser && props.mode === 'edit') {
    form.value = {
      id: newUser.id,
      email: newUser.email,
      full_name: newUser.full_name,
      password: '',
      role: newUser.role as 'admin' | 'staff' | 'customer',
      staff_type: newUser.staff_type || null,
      is_active: newUser.is_active
    };
    // Clear errors
    resetValidation();
    resetSubmissionState();
  } else if (props.mode === 'create') {
    // Reset form for create mode
    resetForm();
  }
}, { immediate: true });

// Watch for modal close to reset form
watch(() => props.isOpen, (isOpen) => {
  if (!isOpen) {
    // Reset form when modal closes
    setTimeout(() => {
      resetForm();
    }, 300); // Wait for modal close animation
  }
});

async function handleSubmit() {
  // Validate form
  const validation = validateForm(form.value);
  
  if (!validation.isValid) {
    errorMessage.value = 'Por favor corrige los errores de validación';
    return;
  }

  const result = await submitUserForm(form.value, props.mode, props.user);
  
  if (result.success) {
    emit('saved');
  } else {
    // Handle validation errors from backend
    if (result.validationErrors) {
      setBackendErrors(result.validationErrors);
    }
  }
}
</script>
